import React, { useState } from "react";
import { Sidebar } from "../components/Sidebar";
import {
  Settings,
  Bell,
  Database,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  RefreshCw,
  Layers,
  ArrowLeft,
  Edit,
  Shield,
  Activity,
  BarChart3,
  TrendingUp,
  Database as DatabaseIcon,
  HardDrive as HardDriveIcon,
  Wifi,
  AlertCircle,
  Info,
  RotateCcw,
  Menu,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useNavigate, useParams } from "@tanstack/react-router";
import { cn } from "../lib/utils";

interface DataSource {
  id: string;
  name: string;
  description: string;
  type: "database" | "api" | "file" | "stream" | "cache";
  status: "connected" | "disconnected" | "error" | "syncing";
  lastSync: Date;
  size: string;
  records: number;
  agents: string[];
  url?: string;
  credentials: "configured" | "missing" | "expired";
  databaseType?:
    | "postgresql"
    | "mysql"
    | "redis"
    | "mongodb"
    | "elasticsearch"
    | "snowflake"
    | "kafka"
    | "rabbitmq"
    | "influxdb"
    | "s3"
    | "gcs"
    | "azure";
  connectionDetails?: {
    host: string;
    port: number;
    database: string;
    username: string;
    ssl: boolean;
    timeout: number;
  };
  performance?: {
    responseTime: number;
    throughput: number;
    errorRate: number;
    uptime: number;
  };
  schema?: {
    tables: number;
    columns: number;
    indexes: number;
  };
  syncHistory?: Array<{
    id: string;
    timestamp: Date;
    status: "success" | "failed" | "partial";
    recordsProcessed: number;
    duration: number;
    error?: string;
  }>;
}

// Utility to format time difference as 'X minutes ago', 'X hours ago', etc.
function formatRelativeTime(date: Date, now: Date) {
  const diff = now.getTime() - date.getTime();
  const absDiff = Math.abs(diff);

  if (absDiff < 60 * 1000) return "Real-time";
  if (absDiff < 60 * 60 * 1000) {
    const mins = Math.round(absDiff / (60 * 1000));
    return `${mins} minute${mins > 1 ? "s" : ""} ago`;
  }
  if (absDiff < 24 * 60 * 60 * 1000) {
    const hours = Math.round(absDiff / (60 * 60 * 1000));
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  }
  const days = Math.round(absDiff / (24 * 60 * 60 * 1000));
  return `${days} day${days > 1 ? "s" : ""} ago`;
}

const now = new Date();
const minutesAgo = (n: number) => new Date(now.getTime() - n * 60 * 1000);
const hoursAgo = (n: number) => new Date(now.getTime() - n * 60 * 60 * 1000);
const daysAgo = (n: number) =>
  new Date(now.getTime() - n * 24 * 60 * 60 * 1000);

// Sample data source details
const sampleDataSourceDetails: DataSource = {
  id: "1",
  name: "Customer Database",
  description: "Primary customer data for support bot training and testing",
  type: "database",
  status: "connected",
  lastSync: minutesAgo(5),
  size: "2.3 GB",
  records: 15420,
  agents: ["Beauty Sales Agent", "Recommendation Engine"],
  url: "postgresql://customer-db.agenttest.com",
  credentials: "configured",
  databaseType: "postgresql",
  connectionDetails: {
    host: "customer-db.agenttest.com",
    port: 5432,
    database: "customer_data",
    username: "agenttest_user",
    ssl: true,
    timeout: 30,
  },
  performance: {
    responseTime: 45,
    throughput: 1250,
    errorRate: 0.02,
    uptime: 99.8,
  },
  schema: {
    tables: 24,
    columns: 156,
    indexes: 18,
  },
  syncHistory: [
    {
      id: "sync-1",
      timestamp: minutesAgo(5),
      status: "success",
      recordsProcessed: 15420,
      duration: 120,
    },
    {
      id: "sync-2",
      timestamp: hoursAgo(1),
      status: "success",
      recordsProcessed: 15420,
      duration: 118,
    },
    {
      id: "sync-3",
      timestamp: hoursAgo(2),
      status: "failed",
      recordsProcessed: 0,
      duration: 0,
      error: "Connection timeout",
    },
    {
      id: "sync-4",
      timestamp: hoursAgo(3),
      status: "success",
      recordsProcessed: 15420,
      duration: 125,
    },
  ],
};

const DataSourceDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { dataSourceId } = useParams({ from: "/data-sources/$dataSourceId" });
  const [activeTab, setActiveTab] = useState<
    "overview" | "performance" | "schema" | "sync" | "settings"
  >("overview");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const dataSource = sampleDataSourceDetails; // In a real app, fetch by ID

  const getStatusColor = (status: string) => {
    switch (status) {
      case "connected":
        return "bg-pink-900/30 text-pink-400 border-pink-600/30";
      case "disconnected":
        return "bg-gray-900/30 text-gray-400 border-gray-600/30";
      case "error":
        return "bg-red-900/30 text-red-400 border-red-600/30";
      case "syncing":
        return "bg-blue-900/30 text-blue-400 border-blue-600/30";
      default:
        return "bg-gray-900/30 text-gray-400 border-gray-600/30";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "connected":
        return <CheckCircle className="h-4 w-4" />;
      case "disconnected":
        return <XCircle className="h-4 w-4" />;
      case "error":
        return <AlertTriangle className="h-4 w-4" />;
      case "syncing":
        return <RefreshCw className="h-4 w-4 animate-spin" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getDatabaseTypeIcon = (databaseType?: string) => {
    switch (databaseType) {
      case "postgresql":
        return (
          <img src="/postgresql.svg" alt="PostgreSQL" className="h-6 w-6" />
        );
      case "redis":
        return <img src="/redis.svg" alt="Redis" className="h-6 w-6" />;
      case "mongodb":
        return <img src="/mongodb.svg" alt="MongoDB" className="h-6 w-6" />;
      case "elasticsearch":
        return (
          <img
            src="/elasticsearch.svg"
            alt="Elasticsearch"
            className="h-6 w-6"
          />
        );
      case "snowflake":
        return (
          <img src="/snowflake-color.svg" alt="Snowflake" className="h-6 w-6" />
        );
      case "s3":
        return <img src="/s3.svg" alt="S3" className="h-6 w-6" />;
      default:
        return <Database className="h-6 w-6 text-white" />;
    }
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: Info },
    { id: "performance", label: "Performance", icon: BarChart3 },
    { id: "schema", label: "Schema", icon: DatabaseIcon },
    { id: "sync", label: "Sync History", icon: RefreshCw },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="flex h-screen bg-gray-900">
      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed lg:relative z-50 h-full transition-transform duration-300 ease-in-out",
          isSidebarOpen
            ? "translate-x-0"
            : "-translate-x-full lg:translate-x-0",
        )}
      >
        <Sidebar />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-full min-h-0 overflow-hidden">
        {/* Top Header */}
        <header className="bg-gray-800 border-b border-gray-700 px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden text-gray-300 hover:bg-gray-700"
                onClick={toggleSidebar}
              >
                <Menu className="h-5 w-5" />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => window.history.back()}
                className="text-gray-400 hover:text-gray-300"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-gray-700 rounded-lg">
                  {getDatabaseTypeIcon(dataSource.databaseType)}
                </div>
                <div>
                  <h1 className="text-xl lg:text-2xl font-bold text-gray-100">
                    {dataSource.name}
                  </h1>
                  <p className="text-gray-400">{dataSource.description}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Badge className={`${getStatusColor(dataSource.status)} border`}>
                {getStatusIcon(dataSource.status)}
                <span className="ml-1">{dataSource.status}</span>
              </Badge>
              <Button
                size="sm"
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <Edit className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Edit</span>
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Sync Now</span>
              </Button>
              <Button
                variant="default"
                size="icon"
                className="bg-gray-700 hover:bg-gray-600 text-gray-300"
              >
                <Bell className="h-5 w-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-300 hover:bg-gray-700"
              >
                <Settings className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </header>

        {/* Tabs */}
        <div className="bg-gray-800 border-b border-gray-700 px-4 lg:px-6 py-3">
          <div className="flex items-center space-x-2 lg:space-x-6 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? "bg-pink-400 text-white"
                      : "text-gray-400 hover:text-gray-300 hover:bg-gray-700"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content Area */}
        <main className="flex-1 h-full flex flex-col min-h-0 bg-gray-900 overflow-auto">
          <div className="p-4 lg:p-6">
            {activeTab === "overview" && (
              <div className="space-y-6">
                {/* Connection Status */}
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-gray-100">
                      Connection Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="flex items-center space-x-3 p-3 bg-gray-700 rounded-lg">
                        <Wifi className="h-5 w-5 text-green-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-300">
                            Connection
                          </p>
                          <p className="text-xs text-gray-400">Active</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 p-3 bg-gray-700 rounded-lg">
                        <Clock className="h-5 w-5 text-blue-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-300">
                            Last Sync
                          </p>
                          <p className="text-xs text-gray-400">
                            {formatRelativeTime(dataSource.lastSync, now)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 p-3 bg-gray-700 rounded-lg">
                        <HardDriveIcon className="h-5 w-5 text-purple-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-300">
                            Size
                          </p>
                          <p className="text-xs text-gray-400">
                            {dataSource.size}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 p-3 bg-gray-700 rounded-lg">
                        <Database className="h-5 w-5 text-yellow-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-300">
                            Records
                          </p>
                          <p className="text-xs text-gray-400">
                            {dataSource.records.toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 p-3 bg-gray-700 rounded-lg">
                        <Shield className="h-5 w-5 text-green-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-300">
                            Credentials
                          </p>
                          <p className="text-xs text-gray-400">
                            {dataSource.credentials}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 p-3 bg-gray-700 rounded-lg">
                        <Activity className="h-5 w-5 text-pink-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-300">
                            Agents
                          </p>
                          <p className="text-xs text-gray-400">
                            {dataSource.agents.length} connected
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Connection Details */}
                {dataSource.connectionDetails && (
                  <Card className="bg-gray-800 border-gray-700">
                    <CardHeader>
                      <CardTitle className="text-gray-100">
                        Connection Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-gray-300">
                            Host
                          </p>
                          <p className="text-sm text-gray-400">
                            {dataSource.connectionDetails.host}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-300">
                            Port
                          </p>
                          <p className="text-sm text-gray-400">
                            {dataSource.connectionDetails.port}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-300">
                            Database
                          </p>
                          <p className="text-sm text-gray-400">
                            {dataSource.connectionDetails.database}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-300">
                            Username
                          </p>
                          <p className="text-sm text-gray-400">
                            {dataSource.connectionDetails.username}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-300">
                            SSL
                          </p>
                          <p className="text-sm text-gray-400">
                            {dataSource.connectionDetails.ssl
                              ? "Enabled"
                              : "Disabled"}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-300">
                            Timeout
                          </p>
                          <p className="text-sm text-gray-400">
                            {dataSource.connectionDetails.timeout}s
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Connected Agents */}
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-gray-100">
                      Connected Agents
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {dataSource.agents.map((agent, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-gray-700 rounded-lg"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            <span className="text-sm text-gray-300">
                              {agent}
                            </span>
                          </div>
                          <Badge
                            variant="secondary"
                            className="text-xs bg-gray-600 text-gray-300"
                          >
                            Active
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === "performance" && dataSource.performance && (
              <div className="space-y-6">
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-gray-100">
                      Performance Metrics
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="p-4 bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Activity className="h-5 w-5 text-blue-400" />
                          <span className="text-sm font-medium text-gray-300">
                            Response Time
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-gray-100 mt-2">
                          {dataSource.performance.responseTime}ms
                        </p>
                      </div>
                      <div className="p-4 bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <TrendingUp className="h-5 w-5 text-green-400" />
                          <span className="text-sm font-medium text-gray-300">
                            Throughput
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-gray-100 mt-2">
                          {dataSource.performance.throughput}/s
                        </p>
                      </div>
                      <div className="p-4 bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <AlertCircle className="h-5 w-5 text-red-400" />
                          <span className="text-sm font-medium text-gray-300">
                            Error Rate
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-gray-100 mt-2">
                          {dataSource.performance.errorRate}%
                        </p>
                      </div>
                      <div className="p-4 bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="h-5 w-5 text-green-400" />
                          <span className="text-sm font-medium text-gray-300">
                            Uptime
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-gray-100 mt-2">
                          {dataSource.performance.uptime}%
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === "schema" && dataSource.schema && (
              <div className="space-y-6">
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-gray-100">
                      Database Schema
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Database className="h-5 w-5 text-blue-400" />
                          <span className="text-sm font-medium text-gray-300">
                            Tables
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-gray-100 mt-2">
                          {dataSource.schema.tables}
                        </p>
                      </div>
                      <div className="p-4 bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Layers className="h-5 w-5 text-green-400" />
                          <span className="text-sm font-medium text-gray-300">
                            Columns
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-gray-100 mt-2">
                          {dataSource.schema.columns}
                        </p>
                      </div>
                      <div className="p-4 bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <BarChart3 className="h-5 w-5 text-purple-400" />
                          <span className="text-sm font-medium text-gray-300">
                            Indexes
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-gray-100 mt-2">
                          {dataSource.schema.indexes}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === "sync" && dataSource.syncHistory && (
              <div className="space-y-6">
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-gray-100">
                      Sync History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {dataSource.syncHistory.map((sync) => (
                        <div
                          key={sync.id}
                          className="flex items-center justify-between p-3 bg-gray-700 rounded-lg"
                        >
                          <div className="flex items-center space-x-3">
                            <div
                              className={`w-2 h-2 rounded-full ${
                                sync.status === "success"
                                  ? "bg-green-400"
                                  : sync.status === "failed"
                                    ? "bg-red-400"
                                    : "bg-yellow-400"
                              }`}
                            ></div>
                            <div>
                              <p className="text-sm font-medium text-gray-300">
                                {formatRelativeTime(sync.timestamp, now)}
                              </p>
                              <p className="text-xs text-gray-400">
                                {sync.recordsProcessed.toLocaleString()} records
                                • {sync.duration}s
                              </p>
                            </div>
                          </div>
                          <Badge
                            className={
                              sync.status === "success"
                                ? "bg-green-900/30 text-green-400 border-green-600/30"
                                : sync.status === "failed"
                                  ? "bg-red-900/30 text-red-400 border-red-600/30"
                                  : "bg-yellow-900/30 text-yellow-400 border-yellow-600/30"
                            }
                          >
                            {sync.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === "settings" && (
              <div className="space-y-6">
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-gray-100">
                      Data Source Settings
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-300">
                          Auto Sync
                        </p>
                        <p className="text-xs text-gray-400">
                          Automatically sync data every 5 minutes
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-gray-600 text-gray-300 hover:bg-gray-700"
                      >
                        Configure
                      </Button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-300">
                          Backup
                        </p>
                        <p className="text-xs text-gray-400">
                          Create backup before sync
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-gray-600 text-gray-300 hover:bg-gray-700"
                      >
                        Configure
                      </Button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-300">
                          Notifications
                        </p>
                        <p className="text-xs text-gray-400">
                          Send alerts on sync failures
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-gray-600 text-gray-300 hover:bg-gray-700"
                      >
                        Configure
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DataSourceDetailsPage;
